import { Dimensions, PixelRatio } from 'react-native-web';
import NetInfo from '@react-native-community/netinfo';
import AsyncStorage from '@react-native-async-storage/async-storage';
import moment from 'moment';
import Alert from 'sweetalert2';

// Configs
import GLOBAL from '../configs/config_global';
import DATA from '../configs/config_data';
import { setGlobalState, STATE_KEYS } from '../configs/config_state';

// region Detect if device is iOS
export function isIOS() {
    return(/(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent));
}
// endregion

// region Function - Camel Case Word to Words
export function camelPad(str = ''){
    return str
        // Look for long acronyms and filter out the last letter
        .replace(/([A-Z]+)([A-Z][a-z])/g, ' $1 $2')
        // Look for lower-case letters followed by upper-case letters
        .replace(/([a-z\d])([A-Z])/g, '$1 $2')
        // Look for lower-case letters followed by numbers
        .replace(/([a-zA-Z])(\d)/g, '$1 $2')
        .replace(/^./, function(str){ return str.toUpperCase(); })
        // Remove any white space left around the word
        .trim();
}
// endregion

// Screen dimensions
const screenWidth = Dimensions.get('window').width, screenHeight = Dimensions.get('window').height;

// region Function - Base 64 to File
export async function base64ToFile(base64 = '') {
    const res = await fetch(base64);
    const buf = await res.arrayBuffer();
    return new File([buf], `image${moment().unix()}.jpeg`, { type: 'image/jpeg' });
}
// endregion

// region Extract Numbers from String
export function extractNumbersOnly(rawText) {
    return rawText.replace(/[^0-9]/g, '');
}
// endregion

export function addThousandsToNumber(text) {
    return(text.toString().replace(/,/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ','));
}

export function getNumberFromStringWithCommas(text) {
    return(Number(text.toString().replace(/,/g, '')));
}

// region Function - Round Number
export function roundNumberToDecimal(num, decimalCount = 0) {
    let multiplyingNum = '1';
    for (let i = 0; i < decimalCount; ++i) {
        multiplyingNum += '0';
    }
    multiplyingNum = Number(multiplyingNum);

    return Math.round((num + Number.EPSILON) * multiplyingNum) / multiplyingNum;
}
// endregion

// region Function - Check if email is part of test account list
export function isTestAccount(email) {
    return(
        email.toLowerCase().includes('@launchdeck.org')
        ||
        DATA.testAccounts.find((item) => item.toLowerCase() === email.toLowerCase())
    );
}
// endregion

// region Get Only Number with Decimals
export function extractNumberWithDecimals(rawText, maxDecimalCount) {
    if (!rawText?.trim()) {
        return('');
    }
    else {
        const text = rawText.replace(/[^0-9.,]+/g, '');
        const decimalSymbol = text.includes(',') ? ',' : (text.includes('.') ? '.' : '');
        const splitText = decimalSymbol ? text.split(decimalSymbol) : [text];

        // Generate new input value
        return maxDecimalCount === 0 ?
            splitText[0]
            :
            (
                splitText[0] +
                decimalSymbol +
                (decimalSymbol ? splitText[1]?.substring(0, maxDecimalCount) : '')
            );
    }
}
// endregion

// region Function - Check array direction (increasing/decreasing/not sorted)
export function getArrayDirection(arr = [], fieldName = '') {
    let isDescending = true,
        isAscending = true;

    for (let i = 0; i < arr.length - 1; ++i) {
        // true if this is greater than the next and all other so far have been true
        isDescending = isDescending && (fieldName ? (arr[i][fieldName] > arr[i + 1]?.[fieldName]) : (arr[i] > arr[i + 1]));

        // true if this is less than the next and all others so far have been true
        isAscending = isAscending && (fieldName ? (arr[i][fieldName] < arr[i + 1]?.[fieldName]) : (arr[i] < arr[i + 1]));
    }

    return(isAscending ? 1 : (isDescending ? 2 : 0));
}
// endregion

// region Function - Show Alert
export function showAlert({ text, confirmButton = null, thirdButton = null, cancelButton = null, showTextInput = null, inputConfigs = {} }) {
    setGlobalState(STATE_KEYS.ALERT_SHOWING, true);
    Alert.fire({
        title: GLOBAL.APP_NAME,
        html: text,
        backdrop: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        allowEnterKey: false,
        showConfirmButton: !!confirmButton,
        confirmButtonText: confirmButton?.text,
        confirmButtonColor: confirmButton?.color || GLOBAL.COLOR.BASIC.UI_BLUE,
        showDenyButton: !!thirdButton,
        denyButtonText: thirdButton?.text,
        denyButtonColor: thirdButton?.color || GLOBAL.COLOR.BASIC.UI_GREEN,
        showCancelButton: !confirmButton ? true : !!cancelButton,
        cancelButtonText: cancelButton?.text || 'OK',
        cancelButtonColor: cancelButton?.color || GLOBAL.COLOR.BASIC.GRAY,
        input: showTextInput ? 'text' : undefined,
        inputValue: inputConfigs?.defaultValue || '',
        inputPlaceholder: inputConfigs?.placeholder || 'Enter text here...',
        closeButtonAriaLabel: 'hello'
    })
        .then((result) => {
            setGlobalState(STATE_KEYS.ALERT_SHOWING, false);
            if (result.isConfirmed) {
                (!!confirmButton?.onPress) && confirmButton?.onPress(result.value);
            }
            else if (result.isDenied) {
                (!!thirdButton?.onPress) && thirdButton?.onPress(result.value);
            }
            else {
                (!!cancelButton?.onPress) && cancelButton?.onPress(result.value);
            }
        });
}

export function showAppError(text, callback = () => {}, showTextOnly = false) {

    if (text === 'Network request failed') {
        showAlert({ text: 'Oops! Connection lost, please try again!' });
    }
    else {
        showAlert({
            text: (showTextOnly ? '' : 'An error has occurred while fetching data, please try again later.<br/><br/>Error message:\n') + text,
            cancelButton: { onPress: callback }
        });
    }
}
// endregion

// region Function - Convert Object to FormData
export function objectToFormData(obj, form, namespace) {
    let fd = form || new FormData(),
        formKey;

    Object.keys(obj).forEach((property) => {
        if (namespace) {
            formKey = namespace + '[' + property + ']';
        }
        else formKey = property;

        // If it's an array
        if (Array.isArray(obj[property])) {
            obj[property].forEach((item) => objectToFormData(item, fd, property));
        }
        // If it's an object type
        else if (isObject(obj[property])) {
            // File
            if (['uri', 'name', 'type'].every((key) => obj[property].hasOwnProperty(key))) {
                fd.append(formKey, obj[property]);
            }
            // Normal object
            else objectToFormData(obj[property], fd, property);
        }
        // String
        else fd.append(formKey, obj[property]);
    });

    return fd;
}
// endregion

// region Function - Validate US Number
export function validateUSNumber(elementValue = '') {
    // Valid US phone number format:
    // +1 NXX-NXX-XXXX
    // N=digits 2–9, X=digits 0–9

    let checkStr = elementValue?.replace(/ /g, '').replace(/[()]/g, '');
    if (typeof elementValue === 'string' && elementValue.trim()) {
        // Remove zip code before checking
        if (elementValue.startsWith('+1')) {
            checkStr = elementValue.substring(2, checkStr.length);
        }
        // If user enters leading 1 and the length of number is 11, they might indicating this as US zip code
        else if (elementValue.startsWith('1') && elementValue.length === 11) {
            checkStr = checkStr.substring(1, checkStr.length);
        }

        // Check for first "N" number
        if (checkStr.startsWith('1') || checkStr.startsWith('0')) return false;

        // Check for second "N" number
        if ((checkStr.substr(3, 1) === '0') || (checkStr.substr(3, 1) === '1')) return false;

        // Regex to test
        let phoneNumberPattern = /^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/;
        return phoneNumberPattern.test(checkStr);
    }
    else return false;
}
// endregion

// region Format US Phone Number
export function formatPhoneNumber(phoneNumberString = '') {
    if (typeof phoneNumberString === 'string' && phoneNumberString.trim().length > 0) {
        if (validateUSNumber(phoneNumberString)) {
            let cleaned = ('' + phoneNumberString.match(/\d/g)?.join('')).replace(/\D/g, '');
            let match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
            if (match) {
                let intlCode = (match[1] ? '+1 ' : '');
                return[intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('').replace('+1', '').trim();
            }
            return phoneNumberString.replace('+1', '').trim();
        }
        else return('');
    }
    else return('');
}
// endregion

// region Function - Replace Object
export function replaceObject(str, replaceObj) {
    let replaceStr = str;
    let regex;

    Object.keys(replaceObj).forEach((key) => {
        regex = new RegExp(key, 'g');
        replaceStr = replaceStr.replace(regex, replaceObj[key]);
    });

    return replaceStr;
}
// endregion

// region Function - Check if device is table on Android
export function checkTabletAndroid() {
    let pixelDensity = PixelRatio.get();
    const adjustedWidth = screenWidth * pixelDensity;
    const adjustedHeight = screenHeight * pixelDensity;
    if (pixelDensity < 2 && (adjustedWidth >= 1000 || adjustedHeight >= 1000)) {
        return true;
    }
    else return(pixelDensity === 2 && (adjustedWidth >= 1824 || adjustedHeight >= 1824));
}
// endregion

// region Function - Get 4 Points from Circle
export function get4PointsAroundCircumference(latitude, longitude, radius) {
    const earthRadius = 6378.1;
    const lat0 = latitude + (-radius / earthRadius) * (180 / Math.PI);
    const lat1 = latitude + (radius / earthRadius) * (180 / Math.PI);
    const lng0 = longitude + (-radius / earthRadius) * (180 / Math.PI) / Math.cos(latitude * Math.PI / 180);
    const lng1 = longitude + (radius / earthRadius) * (180 / Math.PI) / Math.cos(latitude * Math.PI / 180);

    return[
        {
            latitude: lat0,
            longitude: longitude
        }, // bottom
        {
            latitude: latitude,
            longitude: lng0
        }, // left
        {
            latitude: lat1,
            longitude: longitude
        }, // top
        {
            latitude: latitude,
            longitude: lng1
        } // right
    ];
}
// endregion

// region Deep Clone Object
export function deepClone(obj) {
    let copy;

    // Handle the 3 simple types, and null or undefined
    if (obj == null || typeof obj !== 'object') return obj;

    // Handle Date
    if (obj instanceof Date) {
        copy = new Date();
        copy.setTime(obj.getTime());
        return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
        copy = [];
        for (let i = 0, len = obj.length; i < len; ++i) {
            copy = copy.concat(deepClone(obj[i]));
        }
        return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
        copy = {};
        Object.keys(obj).forEach((key) => {
            copy[key] = deepClone(obj[key]);
        });
        return copy;
    }

    throw new Error('Unable to copy obj! Its type isn\'t supported.');
}
// endregion

// region Get Coordinate Delta
export function getDelta(lat, lon, distance) {
    distance = distance / 2;
    const circumference = 40075;
    const oneDegreeOfLatitudeInMeters = 111.32 * 1000;
    const angularDistance = distance / circumference;

    const latitudeDelta = distance / oneDegreeOfLatitudeInMeters;
    const longitudeDelta = Math.abs(Math.atan2(
        Math.sin(angularDistance) * Math.cos(lat),
        Math.cos(angularDistance) - Math.sin(lat) * Math.sin(lat)));

    return({
        latitude: lat,
        longitude: lon,
        latitudeDelta,
        longitudeDelta
    });
}
// endregion

// region Get Social Link ID
export function getSocialLinkID(link) {
    let newLink = link || '';

    newLink = newLink.replace('://', '');

    if (newLink.indexOf('/') > -1) {
        newLink = newLink.split('/').pop();
    }

    // Check for extra URL params
    let questionMarkIndex = newLink.indexOf('?');
    if (questionMarkIndex > -1) {
        newLink = newLink.substring(0, questionMarkIndex);
    }

    // Remove all special symbols
    return(newLink.replace(/[^a-zA-Z0-9._-]/g, ''));
}
// endregion

// region Check if phone number format is correct
export function checkPhoneNumber(number) {
    if (number) {
        let re = /^\+?[1-9]\d{1,14}$/;
        return(re.test(number));
    }
    else return true;
}
// endregion

// region Check Password Strength
export function checkPasswordStrength(password = '') {
    let strongRegex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})');
    let mediumRegex = new RegExp('^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})');

    let hasWhiteSpace = /\s/g.test(password);

    if (hasWhiteSpace) {
        return false;
    }
    else {
        let isStrong = strongRegex.test(password),
            isMedium = mediumRegex.test(password);

        let progress = isStrong ? 1 : (isMedium ? 0.5 : 0.2);

        return(progress);
    }
}
// endregion

// region Check Valid URL
export function checkValidURL(str) {
    if (!str?.trim()) return true;

    const regPattern = new RegExp('^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator


    if (str.indexOf('.') < 0) {
        return false;
    }
    else {
        try {
            return regPattern.test(str);
        }
        catch (e) {
            return false;
        }
    }
}
// endregion

// region Get Day Count
export function getDayCount(startTime, endTime) {
    return(Math.abs(Math.round(moment.duration(moment.unix(endTime).diff(moment.unix(startTime))).asDays())));
}
// endregion

// region Round number to 2 decimals
export function roundToDecimals(x, decimalCount = 2) {
    return(Math.round((x + Number.EPSILON) * (10 * (decimalCount - 1))) / (10 * (decimalCount - 1)));
}
// endregion

// region Get Percentage of Increase
export function getPercentageOfIncrease(previousTotal = 0, total = 0) {
    let isTotalUp = total > previousTotal,
        totalPercent = numberWithCommas(Number(Math.abs(total - previousTotal) / (previousTotal || 1).toFixed(2)) * 100, false, 2);

    return({
        isTotalUp,
        totalPercent
    });
}
// endregion

// region Number of Commas
export function numberWithCommas(x, summary = true, fixedDecimal = 0) {
    let decimalStr = Number(x || '0').toFixed(fixedDecimal),
        processingNumber = summary ? Math.round(x || 0) : (fixedDecimal > 0 ? Number(decimalStr.substring(0, decimalStr.length - (fixedDecimal + 1))) : x),
        postDecimal = fixedDecimal > 0 ? decimalStr.substring(decimalStr.toString().length - 3) : '';

    let postfix = '';
    if (summary) {
        if (processingNumber >= 1000 && processingNumber < 1000000) {
            postfix = 'K';
            processingNumber = Math.round(processingNumber / 100) / 10;
        }
        else if (processingNumber >= 1000000 && processingNumber < 1000000000) {
            postfix = 'M';
            processingNumber = Math.round(processingNumber / 100000) / 10;
        }
        else if (processingNumber >= 1000000000 && processingNumber < 1000000000000) {
            postfix = 'B';
            processingNumber = Math.round(processingNumber / 100000000) / 10;
        }
    }
    else if (fixedDecimal > 0) {
        postfix = postDecimal;
    }

    return processingNumber.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + postfix;
}
// endregion

// region Generate name initials
export function generateInitials(text = '') {
    let name = text.toUpperCase().split(' ');
    let initials = '';

    if (name.length === 1) {
        initials = `${name[0].charAt(0)}`;
    }
    else if (name.length > 1) {
        initials = `${name[0].charAt(0)}${name[1].charAt(0)}`;
    }
    else initials = '';

    return(initials);
}

export function generateColor(text = '') {
    let sumChars = 0;
    for (let i = 0; i < text.length; i++) {
        sumChars += text.charCodeAt(i);
    }

    // inspired by https://github.com/wbinnssmith/react-user-avatar
    // colors from https://flatuicolors.com/
    const colors = [
        '#e67e22', // carrot
        '#2ecc71', // emerald
        '#3498db', // peter river
        '#8e44ad', // wisteria
        '#e74c3c', // alizarin
        '#1abc9c', // turquoise
        '#2c3e50' // midnight blue
    ];

    return(colors[sumChars % colors.length]);
}
// endregion

// region Capitalize Each Word
export function toTitleCase(str) {
    return str.replace(/\w\S*/g, function(txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1);
    });
}
// endregion

// Check if variable is object
export function isObject(value) {
    return(value && typeof value === 'object' && value.constructor === Object);
}

// Check if email is correctly formatted
export function checkEmail(email = '') {
    let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!email || !email.trim()) {
        return true;
    }
    else return re.test(email);
}

// Check internet connection
export function checkInternet() {
    return new Promise((resolve) => {
        NetInfo.fetch()
            .then((connectionState) => {
                console.log('[SUCCESS] CHECK INTERNET CONNECTION', connectionState);
                resolve(connectionState.isConnected);
            });
    });
}

export function storeData(keyName, keyData) {
    return new Promise((resolve) => {
        AsyncStorage.setItem(keyName, keyData)
            .then((result) => {
                console.log('[SUCCESS] Save Data to Storage', keyName);
                resolve(result);
            })
            .catch((error) => {
                console.log('[FAILED] Save Data to Storage', error);
                resolve(null);
            });
    });
}
// endregion

// region Function - Get Data
export function getData(keyName) {
    return new Promise((resolve) => {
        AsyncStorage.getItem(keyName)
            .then((value) => {
                if (value) {
                    try {
                        console.log('[SUCCESS] Get data from storage "' + keyName + '"', JSON.parse(value));
                    }
                    catch {
                        console.log('[SUCCESS] Get data from storage "' + keyName + '"', value);
                    }

                    resolve(value);
                }
                else {
                    console.log('[SUCCESS] Get data from storage "' + keyName + '"', null);
                    resolve(null);
                }
            })
            .catch((error) => {
                console.log('[FAILED] Get data from storage "' + keyName + '"', error);
                resolve(null);
            });
    });
}
// endregion

// region Function - Remove Data
export function removeData(keyName) {
    AsyncStorage.removeItem(keyName)
        .then(() => console.log('[SUCCESS] Remove data from storage "' + keyName + '"'))
        .catch((error) => console.log('[FAILED - ERROR] Remove data from storage "' + keyName + '"', error));;
}
// endregion

// region Function - Check if scroll is close to bottom
export function isCloseToBottom({ layoutMeasurement, contentOffset, contentSize }) {
    const paddingToBottom = 20;
    return layoutMeasurement.height + contentOffset.y >=
        contentSize.height - paddingToBottom;
}

export function isCloseToTop({ layoutMeasurement, contentOffset }) {
    const paddingToTop = Dimensions.get('window').height + 20;
    return contentOffset.y <= paddingToTop;
}
// endregion
